import {memo, forwardRef} from 'react';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';

interface TextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  maxLength?: number;
  error?: boolean;
  fullWidth?: boolean;
  resize?: boolean;
}

const StyledTextarea = styled.textarea<{
  $error?: boolean;
  $fullWidth?: boolean;
  $resize?: boolean;
}>`
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  padding: 12px 16px;
  border: 2px solid
    ${props => (props.$error ? Palette.primaryTerraCotta : '#e0e0e0')};
  border-radius: 8px;
  font-size: 15px;
  line-height: 1.5;
  font-family: inherit;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  resize: ${props => (props.$resize ? 'vertical' : 'none')};
  min-height: 80px;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    border-color: ${props =>
      props.$error ? Palette.primaryTerraCotta : Palette.primaryTerraCotta};
    box-shadow: 0 0 0 3px
      ${props =>
        props.$error ? 'rgba(224, 71, 48, 0.1)' : 'rgba(224, 71, 48, 0.1)'};
  }

  &:disabled {
    background: #f8f9fa;
    border-color: #e9ecef;
    color: #666;
    cursor: not-allowed;
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: #ccc;
  }
`;

const CharacterCount = styled.div<{$isOverLimit?: boolean}>`
  font-size: 12px;
  color: ${props => (props.$isOverLimit ? Palette.primaryTerraCotta : '#666')};
  text-align: right;
  margin-top: 4px;
`;

const Container = styled.div<{$fullWidth?: boolean}>`
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  display: flex;
  flex-direction: column;
`;

export const Textarea = memo(
  forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
      {
        value,
        onChange,
        placeholder,
        disabled = false,
        rows = 4,
        maxLength,
        error = false,
        fullWidth = true,
        resize = true,
        ...rest
      },
      ref,
    ) => {
      const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
      };

      const isOverLimit = maxLength ? value.length > maxLength : false;

      return (
        <Container $fullWidth={fullWidth}>
          <StyledTextarea
            ref={ref}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            maxLength={maxLength}
            $error={error || isOverLimit}
            $fullWidth={fullWidth}
            $resize={resize}
            {...rest}
          />
          {maxLength && (
            <CharacterCount $isOverLimit={isOverLimit}>
              {value.length}/{maxLength}
            </CharacterCount>
          )}
        </Container>
      );
    },
  ),
);
